/**
 * Domain enums. These are kept in lock-step with the Prisma enums in
 * apps/api/prisma/schema.prisma — if you change one, change the other.
 */

export const UserRole = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  STAFF: 'STAFF',
  EMPLOYER: 'EMPLOYER',
  SEEKER: 'SEEKER',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  BLOCKED: 'BLOCKED',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const CompanyRole = {
  OWNER: 'OWNER',
  RECRUITER: 'RECRUITER',
  VIEWER: 'VIEWER',
} as const;
export type CompanyRole = (typeof CompanyRole)[keyof typeof CompanyRole];

/**
 * Which of the two audiences a candidate is here for.
 *
 * Declared here as well as in the Prisma schema because this package is
 * deliberately framework-free — nothing in `packages/shared` imports from
 * `@prisma/client`, so profile scoring can be written and tested without a
 * generated client or a database.
 *
 * Two values, not three. Supervisory work is a property of the JOB — the
 * `supervisor-line-leader` category already exists — so classifying a welder
 * as supervisory the moment they apply for a team-leader post would describe
 * the vacancy by mislabelling the person.
 */
export const WorkerType = {
  /** Operator, technician, welder, assembly, warehouse, helper. */
  INDUSTRIAL: 'INDUSTRIAL',
  /** Engineer, supervisor, IT, quality, design, maintenance, management. */
  PROFESSIONAL: 'PROFESSIONAL',
} as const;
export type WorkerType = (typeof WorkerType)[keyof typeof WorkerType];

/**
 * What a job asks of an applicant.
 *
 * A tier rather than a per-requirement checklist, deliberately. A checklist
 * lets whoever posts a vacancy make an operator role demand a CV by ticking a
 * box, and eventually someone does — while the cost of that mistake falls
 * entirely on a candidate who has never owned one. A tier is a single choice
 * whose consequences are legible at the moment of making it.
 */
export const ApplicationTier = {
  /** The profile is enough. What every job does today. */
  QUICK: 'QUICK',
  /** Profile plus the job's own screening questions. */
  STANDARD: 'STANDARD',
  /** Profile plus a résumé. The only tier that may require a document. */
  PROFESSIONAL: 'PROFESSIONAL',
} as const;
export type ApplicationTier = (typeof ApplicationTier)[keyof typeof ApplicationTier];

export const VerificationStatus = {
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
  UNDER_REVIEW: 'UNDER_REVIEW',
  ACTION_REQUIRED: 'ACTION_REQUIRED',
} as const;
export type VerificationStatus = (typeof VerificationStatus)[keyof typeof VerificationStatus];

/**
 * Education ladder: 10th up to any degree.
 *
 * Two mechanisms exist for matching, because manufacturing hiring needs both:
 *  - `minQualification` on a Job is a FLOOR ("10th and above") — compared via
 *    QUALIFICATION_RANK so the ladder is orderable in a plain SQL `>=`.
 *  - `acceptedQualifications[]` on a Job is an EXPLICIT SET ("ITI or Diploma
 *    only") — used when a floor would wrongly admit over-qualified or
 *    unrelated candidates.
 * A Job may set either or both; both must pass when both are present.
 */
export const EducationLevel = {
  TENTH: 'TENTH', // 10th / SSLC / Matriculation
  TWELFTH: 'TWELFTH', // 12th / HSC / Intermediate
  ITI: 'ITI', // ITI — NTC / NAC
  DIPLOMA: 'DIPLOMA', // Polytechnic diploma
  UNDERGRADUATE: 'UNDERGRADUATE', // BA / BSc / BCom / BBA / BCA
  ENGINEERING_UG: 'ENGINEERING_UG', // BE / BTech
  POSTGRADUATE: 'POSTGRADUATE', // MA / MSc / MCom / MBA / MCA
  ENGINEERING_PG: 'ENGINEERING_PG', // ME / MTech
  DOCTORATE: 'DOCTORATE', // PhD
} as const;
export type EducationLevel = (typeof EducationLevel)[keyof typeof EducationLevel];

/** Ordinal rank used for ">= minimum qualification" filtering. */
export const QUALIFICATION_RANK: Record<EducationLevel, number> = {
  TENTH: 10,
  TWELFTH: 12,
  ITI: 13,
  DIPLOMA: 15,
  UNDERGRADUATE: 17,
  ENGINEERING_UG: 18,
  POSTGRADUATE: 19,
  ENGINEERING_PG: 20,
  DOCTORATE: 22,
};

export const QUALIFICATION_LABEL: Record<EducationLevel, string> = {
  TENTH: '10th / SSLC',
  TWELFTH: '12th / HSC',
  ITI: 'ITI (NTC / NAC)',
  DIPLOMA: 'Diploma (Polytechnic)',
  UNDERGRADUATE: 'Degree (BA / BSc / BCom / BBA / BCA)',
  ENGINEERING_UG: 'BE / BTech',
  POSTGRADUATE: 'PG (MA / MSc / MCom / MBA / MCA)',
  ENGINEERING_PG: 'ME / MTech',
  DOCTORATE: 'PhD',
};

/** Ladder order, lowest first — safe for dropdowns. */
export const QUALIFICATION_ORDER: EducationLevel[] = (
  Object.keys(QUALIFICATION_RANK) as EducationLevel[]
).sort((a, b) => QUALIFICATION_RANK[a] - QUALIFICATION_RANK[b]);

export const StudyMode = {
  REGULAR: 'REGULAR',
  PART_TIME: 'PART_TIME',
  DISTANCE: 'DISTANCE',
} as const;
export type StudyMode = (typeof StudyMode)[keyof typeof StudyMode];

export const SkillCategory = {
  CNC: 'CNC',
  SMT: 'SMT',
  WELDING: 'WELDING',
  ASSEMBLY: 'ASSEMBLY',
  QUALITY: 'QUALITY',
  MAINTENANCE: 'MAINTENANCE',
  PRODUCTION: 'PRODUCTION',
  ELECTRICAL: 'ELECTRICAL',
  ELECTRONICS: 'ELECTRONICS',
  MECHANICAL: 'MECHANICAL',
  TOOL_ROOM: 'TOOL_ROOM',
  PAINT_SHOP: 'PAINT_SHOP',
  MOULDING: 'MOULDING',
  PACKING: 'PACKING',
  WAREHOUSE: 'WAREHOUSE',
  SAFETY: 'SAFETY',
  COMPUTER: 'COMPUTER',
  SOFT_SKILL: 'SOFT_SKILL',
  OTHER: 'OTHER',
} as const;
export type SkillCategory = (typeof SkillCategory)[keyof typeof SkillCategory];

export const Proficiency = {
  BEGINNER: 'BEGINNER',
  INTERMEDIATE: 'INTERMEDIATE',
  ADVANCED: 'ADVANCED',
  EXPERT: 'EXPERT',
} as const;
export type Proficiency = (typeof Proficiency)[keyof typeof Proficiency];

export const Gender = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'OTHER',
} as const;
export type Gender = (typeof Gender)[keyof typeof Gender];

export const GenderPreference = {
  ANY: 'ANY',
  MALE: 'MALE',
  FEMALE: 'FEMALE',
} as const;
export type GenderPreference = (typeof GenderPreference)[keyof typeof GenderPreference];

export const MaritalStatus = {
  SINGLE: 'SINGLE',
  MARRIED: 'MARRIED',
  OTHER: 'OTHER',
} as const;
export type MaritalStatus = (typeof MaritalStatus)[keyof typeof MaritalStatus];

export const ShiftType = {
  GENERAL: 'GENERAL',
  DAY: 'DAY',
  NIGHT: 'NIGHT',
  ROTATIONAL: 'ROTATIONAL',
  ANY: 'ANY',
} as const;
export type ShiftType = (typeof ShiftType)[keyof typeof ShiftType];

export const EmploymentType = {
  PERMANENT: 'PERMANENT',
  CONTRACT: 'CONTRACT',
  TRAINEE: 'TRAINEE',
  APPRENTICE: 'APPRENTICE',
  TEMPORARY: 'TEMPORARY',
  INTERNSHIP: 'INTERNSHIP',
} as const;
export type EmploymentType = (typeof EmploymentType)[keyof typeof EmploymentType];

export const SalaryPeriod = {
  MONTHLY: 'MONTHLY',
  DAILY: 'DAILY',
  HOURLY: 'HOURLY',
  YEARLY: 'YEARLY',
} as const;
export type SalaryPeriod = (typeof SalaryPeriod)[keyof typeof SalaryPeriod];

export const JobStatus = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  PUBLISHED: 'PUBLISHED',
  PAUSED: 'PAUSED',
  CLOSED: 'CLOSED',
  REJECTED: 'REJECTED',
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

/**
 * The recruitment funnel. Order matters — it drives the pipeline board and the
 * "vacancy vs fulfilment" report.
 */
export const ApplicationStatus = {
  APPLIED: 'APPLIED',
  SCREENING: 'SCREENING',
  SHORTLISTED: 'SHORTLISTED',
  INTERVIEW_SCHEDULED: 'INTERVIEW_SCHEDULED',
  INTERVIEWED: 'INTERVIEWED',
  SELECTED: 'SELECTED',
  MEDICAL_PENDING: 'MEDICAL_PENDING',
  MEDICAL_CLEARED: 'MEDICAL_CLEARED',
  BGV_PENDING: 'BGV_PENDING',
  BGV_CLEARED: 'BGV_CLEARED',
  OFFER_ISSUED: 'OFFER_ISSUED',
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
  OFFER_DECLINED: 'OFFER_DECLINED',
  JOINED: 'JOINED',
  DEPLOYED: 'DEPLOYED',
  REJECTED: 'REJECTED',
  WITHDRAWN: 'WITHDRAWN',
  NO_SHOW: 'NO_SHOW',
  ON_HOLD: 'ON_HOLD',
} as const;
export type ApplicationStatus = (typeof ApplicationStatus)[keyof typeof ApplicationStatus];

export const APPLICATION_FUNNEL: ApplicationStatus[] = [
  'APPLIED',
  'SCREENING',
  'SHORTLISTED',
  'INTERVIEW_SCHEDULED',
  'INTERVIEWED',
  'SELECTED',
  'MEDICAL_PENDING',
  'MEDICAL_CLEARED',
  'BGV_PENDING',
  'BGV_CLEARED',
  'OFFER_ISSUED',
  'OFFER_ACCEPTED',
  'JOINED',
  'DEPLOYED',
];

/** Statuses that end the pipeline for a candidate. */
export const TERMINAL_APPLICATION_STATUSES: ApplicationStatus[] = [
  'REJECTED',
  'WITHDRAWN',
  'NO_SHOW',
  'OFFER_DECLINED',
];

/**
 * Legal state transitions. The service layer refuses anything not listed here,
 * so the funnel can never go backwards by accident.
 */
export const APPLICATION_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  APPLIED: ['SCREENING', 'SHORTLISTED', 'REJECTED', 'WITHDRAWN', 'ON_HOLD'],
  SCREENING: ['SHORTLISTED', 'REJECTED', 'WITHDRAWN', 'ON_HOLD'],
  SHORTLISTED: ['INTERVIEW_SCHEDULED', 'SELECTED', 'REJECTED', 'WITHDRAWN', 'ON_HOLD'],
  INTERVIEW_SCHEDULED: ['INTERVIEWED', 'NO_SHOW', 'REJECTED', 'WITHDRAWN', 'ON_HOLD'],
  INTERVIEWED: ['SELECTED', 'REJECTED', 'ON_HOLD', 'WITHDRAWN'],
  SELECTED: ['MEDICAL_PENDING', 'BGV_PENDING', 'OFFER_ISSUED', 'REJECTED', 'WITHDRAWN'],
  MEDICAL_PENDING: ['MEDICAL_CLEARED', 'REJECTED', 'WITHDRAWN'],
  MEDICAL_CLEARED: ['BGV_PENDING', 'OFFER_ISSUED', 'REJECTED', 'WITHDRAWN'],
  BGV_PENDING: ['BGV_CLEARED', 'REJECTED', 'WITHDRAWN'],
  BGV_CLEARED: ['OFFER_ISSUED', 'REJECTED', 'WITHDRAWN'],
  OFFER_ISSUED: ['OFFER_ACCEPTED', 'OFFER_DECLINED', 'WITHDRAWN'],
  OFFER_ACCEPTED: ['JOINED', 'NO_SHOW', 'WITHDRAWN'],
  OFFER_DECLINED: [],
  JOINED: ['DEPLOYED'],
  DEPLOYED: [],
  REJECTED: [],
  WITHDRAWN: [],
  NO_SHOW: ['INTERVIEW_SCHEDULED', 'REJECTED'],
  ON_HOLD: ['SCREENING', 'SHORTLISTED', 'INTERVIEW_SCHEDULED', 'REJECTED', 'WITHDRAWN'],
};

export const ApplicationSource = {
  SEEKER_APPLIED: 'SEEKER_APPLIED',
  ADMIN_ASSIGNED: 'ADMIN_ASSIGNED',
  BULK_IMPORT: 'BULK_IMPORT',
  REFERRAL: 'REFERRAL',
  WALK_IN: 'WALK_IN',
} as const;
export type ApplicationSource = (typeof ApplicationSource)[keyof typeof ApplicationSource];

export const InterviewMode = {
  IN_PERSON: 'IN_PERSON',
  TELEPHONIC: 'TELEPHONIC',
  VIDEO: 'VIDEO',
  WALK_IN: 'WALK_IN',
} as const;
export type InterviewMode = (typeof InterviewMode)[keyof typeof InterviewMode];

export const InterviewStatus = {
  SCHEDULED: 'SCHEDULED',
  RESCHEDULED: 'RESCHEDULED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NO_SHOW: 'NO_SHOW',
} as const;
export type InterviewStatus = (typeof InterviewStatus)[keyof typeof InterviewStatus];

export const InterviewResult = {
  PENDING: 'PENDING',
  SELECTED: 'SELECTED',
  REJECTED: 'REJECTED',
  ON_HOLD: 'ON_HOLD',
} as const;
export type InterviewResult = (typeof InterviewResult)[keyof typeof InterviewResult];

export const DocumentType = {
  RESUME: 'RESUME',
  PHOTO: 'PHOTO',
  AADHAAR: 'AADHAAR',
  PAN: 'PAN',
  MARKSHEET_10: 'MARKSHEET_10',
  MARKSHEET_12: 'MARKSHEET_12',
  ITI_CERTIFICATE: 'ITI_CERTIFICATE',
  DIPLOMA_CERTIFICATE: 'DIPLOMA_CERTIFICATE',
  DEGREE_CERTIFICATE: 'DEGREE_CERTIFICATE',
  EXPERIENCE_LETTER: 'EXPERIENCE_LETTER',
  RELIEVING_LETTER: 'RELIEVING_LETTER',
  PAYSLIP: 'PAYSLIP',
  BANK_PASSBOOK: 'BANK_PASSBOOK',
  UAN_CARD: 'UAN_CARD',
  ESI_CARD: 'ESI_CARD',
  MEDICAL_REPORT: 'MEDICAL_REPORT',
  POLICE_VERIFICATION: 'POLICE_VERIFICATION',
  OFFER_LETTER: 'OFFER_LETTER',
  APPOINTMENT_LETTER: 'APPOINTMENT_LETTER',
  COMPANY_GST: 'COMPANY_GST',
  COMPANY_INCORPORATION: 'COMPANY_INCORPORATION',
  COMPANY_PAN: 'COMPANY_PAN',
  OTHER: 'OTHER',
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

/** Document types holding regulated identifiers — encrypted at rest, masked in API responses. */
export const SENSITIVE_DOCUMENT_TYPES: DocumentType[] = ['AADHAAR', 'PAN', 'BANK_PASSBOOK'];

export const DocumentOwnerType = {
  SEEKER: 'SEEKER',
  COMPANY: 'COMPANY',
  APPLICATION: 'APPLICATION',
} as const;
export type DocumentOwnerType = (typeof DocumentOwnerType)[keyof typeof DocumentOwnerType];

export const RequirementStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  FULFILLED: 'FULFILLED',
  PARTIALLY_FULFILLED: 'PARTIALLY_FULFILLED',
  ON_HOLD: 'ON_HOLD',
  CANCELLED: 'CANCELLED',
} as const;
export type RequirementStatus = (typeof RequirementStatus)[keyof typeof RequirementStatus];

export const MedicalStatus = {
  PENDING: 'PENDING',
  SCHEDULED: 'SCHEDULED',
  FIT: 'FIT',
  UNFIT: 'UNFIT',
  CONDITIONALLY_FIT: 'CONDITIONALLY_FIT',
} as const;
export type MedicalStatus = (typeof MedicalStatus)[keyof typeof MedicalStatus];

export const BgvStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  CLEARED: 'CLEARED',
  DISCREPANCY: 'DISCREPANCY',
  FAILED: 'FAILED',
} as const;
export type BgvStatus = (typeof BgvStatus)[keyof typeof BgvStatus];

export const OfferStatus = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  REVOKED: 'REVOKED',
  EXPIRED: 'EXPIRED',
} as const;
export type OfferStatus = (typeof OfferStatus)[keyof typeof OfferStatus];

export const DeploymentStatus = {
  JOINED: 'JOINED',
  ACTIVE: 'ACTIVE',
  ON_NOTICE: 'ON_NOTICE',
  TRANSFERRED: 'TRANSFERRED',
  EXITED: 'EXITED',
  ABSCONDED: 'ABSCONDED',
  TERMINATED: 'TERMINATED',
  REPLACED: 'REPLACED',
} as const;
export type DeploymentStatus = (typeof DeploymentStatus)[keyof typeof DeploymentStatus];

export const ExitReason = {
  RESIGNATION: 'RESIGNATION',
  ABSCONDING: 'ABSCONDING',
  TERMINATION: 'TERMINATION',
  CONTRACT_END: 'CONTRACT_END',
  MEDICAL: 'MEDICAL',
  TRANSFER: 'TRANSFER',
  CLIENT_RELEASE: 'CLIENT_RELEASE',
  OTHER: 'OTHER',
} as const;
export type ExitReason = (typeof ExitReason)[keyof typeof ExitReason];

// --- Phase 2: attendance ---------------------------------------------------

/**
 * What a supervisor marks against a deployed worker for one calendar day.
 *
 * Payability is explicit rather than inferred from an hours count, because an
 * invoice line is computed from it: PAID_LEAVE, HOLIDAY and WEEKLY_OFF are
 * payable at full rate, HALF_DAY at half, ABSENT and UNPAID_LEAVE at nothing.
 */
export const AttendanceStatus = {
  PRESENT: 'PRESENT',
  HALF_DAY: 'HALF_DAY',
  ABSENT: 'ABSENT',
  WEEKLY_OFF: 'WEEKLY_OFF',
  HOLIDAY: 'HOLIDAY',
  PAID_LEAVE: 'PAID_LEAVE',
  UNPAID_LEAVE: 'UNPAID_LEAVE',
  /** Working, but off-site — training, another client, an errand for the client. */
  ON_DUTY: 'ON_DUTY',
} as const;
export type AttendanceStatus = (typeof AttendanceStatus)[keyof typeof AttendanceStatus];

export const ATTENDANCE_STATUS_LABEL: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  HALF_DAY: 'Half day',
  ABSENT: 'Absent',
  WEEKLY_OFF: 'Weekly off',
  HOLIDAY: 'Holiday',
  PAID_LEAVE: 'Paid leave',
  UNPAID_LEAVE: 'Unpaid leave',
  ON_DUTY: 'On duty',
};

/** Single-letter marks for the muster grid, as used on a paper register. */
export const ATTENDANCE_STATUS_CODE: Record<AttendanceStatus, string> = {
  PRESENT: 'P',
  HALF_DAY: '½',
  ABSENT: 'A',
  WEEKLY_OFF: 'W',
  HOLIDAY: 'H',
  PAID_LEAVE: 'PL',
  UNPAID_LEAVE: 'UL',
  ON_DUTY: 'OD',
};

/**
 * Fraction of a day's billing each status earns.
 *
 * The single source of truth for "what does this day bill?" — payroll, the
 * invoice and the on-screen summary all read it, so they cannot drift apart.
 */
export const ATTENDANCE_PAYABLE_FRACTION: Record<AttendanceStatus, number> = {
  PRESENT: 1,
  ON_DUTY: 1,
  WEEKLY_OFF: 1,
  HOLIDAY: 1,
  PAID_LEAVE: 1,
  HALF_DAY: 0.5,
  ABSENT: 0,
  UNPAID_LEAVE: 0,
};

export const AttendanceApproval = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type AttendanceApproval = (typeof AttendanceApproval)[keyof typeof AttendanceApproval];

/**
 * Lifecycle of one client's attendance for one month. LOCKED means invoiced —
 * the days beneath it must stop moving, or the invoice and the muster stop
 * agreeing and the client disputes it.
 */
export const AttendancePeriodStatus = {
  OPEN: 'OPEN',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  LOCKED: 'LOCKED',
} as const;
export type AttendancePeriodStatus =
  (typeof AttendancePeriodStatus)[keyof typeof AttendancePeriodStatus];

/**
 * DRAFT is the only editable state and the only one that can be deleted; once a
 * number is issued to a client it stays in the books, and a mistake is corrected
 * by cancelling (with a reason) rather than by rewriting history.
 */
export const InvoiceStatus = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  ISSUED: 'Issued',
  PARTIALLY_PAID: 'Part paid',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
};

/** An invoice in these states is money we are still owed. */
export const OPEN_INVOICE_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.ISSUED,
  InvoiceStatus.PARTIALLY_PAID,
];

/**
 * How a deployment's charge is worked out.
 *
 * MONTHLY pro-rates an agreed monthly rate by the days actually worked; DAILY
 * multiplies an agreed day rate. Indian manufacturing contracts use both, often
 * within the same client — staff on monthly, casual labour on daily.
 */
export const RateBasis = {
  MONTHLY: 'MONTHLY',
  DAILY: 'DAILY',
} as const;
export type RateBasis = (typeof RateBasis)[keyof typeof RateBasis];

export const PaymentMethod = {
  BANK_TRANSFER: 'BANK_TRANSFER',
  CHEQUE: 'CHEQUE',
  UPI: 'UPI',
  CASH: 'CASH',
  ADJUSTMENT: 'ADJUSTMENT',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  BANK_TRANSFER: 'Bank transfer',
  CHEQUE: 'Cheque',
  UPI: 'UPI',
  CASH: 'Cash',
  ADJUSTMENT: 'Adjustment / credit note',
};

/**
 * Lifecycle of one payroll run.
 *
 * DRAFT is recomputable from the muster as many times as you like; APPROVED
 * freezes the figures because that is the point at which a bank transfer file
 * goes out; PAID records that the money actually left. A mistake after approval
 * is corrected by cancelling the run and raising a fresh one, never by editing
 * a payslip a worker has already been shown.
 */
export const PayrollRunStatus = {
  DRAFT: 'DRAFT',
  APPROVED: 'APPROVED',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
} as const;
export type PayrollRunStatus = (typeof PayrollRunStatus)[keyof typeof PayrollRunStatus];

export const PAYROLL_RUN_STATUS_LABEL: Record<PayrollRunStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
};

/** A run in these states still owes wages to somebody. */
export const OPEN_PAYROLL_STATUSES: PayrollRunStatus[] = [
  PayrollRunStatus.DRAFT,
  PayrollRunStatus.APPROVED,
];

/**
 * How a worker's wages left the building.
 *
 * Kept separate from `PaymentMethod` — that one records money coming *in* from
 * a client, and the two lists genuinely differ: a salary goes out by NEFT or
 * cash, never by "adjustment / credit note".
 */
export const DisbursementMode = {
  BANK_TRANSFER: 'BANK_TRANSFER',
  UPI: 'UPI',
  CHEQUE: 'CHEQUE',
  CASH: 'CASH',
} as const;
export type DisbursementMode = (typeof DisbursementMode)[keyof typeof DisbursementMode];

export const DISBURSEMENT_MODE_LABEL: Record<DisbursementMode, string> = {
  BANK_TRANSFER: 'Bank transfer',
  UPI: 'UPI',
  CHEQUE: 'Cheque',
  CASH: 'Cash',
};

export const NotificationChannel = {
  SMS: 'SMS',
  WHATSAPP: 'WHATSAPP',
  EMAIL: 'EMAIL',
  IN_APP: 'IN_APP',
  PUSH: 'PUSH',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const NotificationStatus = {
  QUEUED: 'QUEUED',
  SENT: 'SENT',
  DELIVERED: 'DELIVERED',
  READ: 'READ',
  FAILED: 'FAILED',
} as const;
export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus];

/**
 * What a person is reporting. The list is what candidates actually report, in
 * the words they use — "someone asked me for money" is one category rather than
 * a sub-case of fraud, because it is the overwhelming majority of reports and
 * burying it in a generic bucket loses the triage signal.
 */
export const FraudReportCategory = {
  MONEY_DEMANDED: 'MONEY_DEMANDED',
  FAKE_JOB: 'FAKE_JOB',
  FAKE_EMPLOYER: 'FAKE_EMPLOYER',
  SALARY_MISMATCH: 'SALARY_MISMATCH',
  SUSPICIOUS_CONTACT: 'SUSPICIOUS_CONTACT',
  FAKE_OFFER_LETTER: 'FAKE_OFFER_LETTER',
  DATA_MISUSE: 'DATA_MISUSE',
  OTHER: 'OTHER',
} as const;
export type FraudReportCategory = (typeof FraudReportCategory)[keyof typeof FraudReportCategory];

export const FraudReportStatus = {
  NEW: 'NEW',
  UNDER_REVIEW: 'UNDER_REVIEW',
  ACTION_TAKEN: 'ACTION_TAKEN',
  DISMISSED: 'DISMISSED',
} as const;
export type FraudReportStatus = (typeof FraudReportStatus)[keyof typeof FraudReportStatus];

/**
 * Fine-grained permissions for ADMIN/STAFF users. SUPER_ADMIN implicitly holds
 * all of them; STAFF hold only what is granted in StaffPermission.
 */
export const Permission = {
  DASHBOARD_VIEW: 'dashboard:view',
  EMPLOYER_VIEW: 'employer:view',
  EMPLOYER_APPROVE: 'employer:approve',
  EMPLOYER_BLOCK: 'employer:block',
  SEEKER_VIEW: 'seeker:view',
  SEEKER_MANAGE: 'seeker:manage',
  DOCUMENT_VERIFY: 'document:verify',
  DOCUMENT_VIEW_SENSITIVE: 'document:view_sensitive',
  JOB_VIEW: 'job:view',
  JOB_MANAGE: 'job:manage',
  JOB_APPROVE: 'job:approve',
  APPLICATION_VIEW: 'application:view',
  APPLICATION_MANAGE: 'application:manage',
  INTERVIEW_MANAGE: 'interview:manage',
  RECRUITMENT_MANAGE: 'recruitment:manage',
  DEPLOYMENT_MANAGE: 'deployment:manage',
  ATTENDANCE_VIEW: 'attendance:view',
  ATTENDANCE_MANAGE: 'attendance:manage',
  /// Approving a month is what makes it billable, so it is a separate grant
  /// from marking it — the person who marks should not be the one who signs off.
  ATTENDANCE_APPROVE: 'attendance:approve',
  BILLING_VIEW: 'billing:view',
  /// Raising and cancelling invoices.
  BILLING_MANAGE: 'billing:manage',
  /// Recording money received. Separated from BILLING_MANAGE so the person who
  /// raises an invoice is not the person who can mark it paid.
  PAYMENT_RECORD: 'payment:record',
  /// Reading payslips and runs. Separate from BILLING_VIEW because what a
  /// worker is paid and what a client is billed are different secrets — the
  /// margin between them is visible to anyone holding both.
  PAYROLL_VIEW: 'payroll:view',
  /// Preparing and recomputing a draft run.
  PAYROLL_MANAGE: 'payroll:manage',
  /// Approving a run and marking it disbursed. Held apart from PAYROLL_MANAGE
  /// so the person who computes wages is not the person who releases them.
  PAYROLL_APPROVE: 'payroll:approve',
  CONTENT_MANAGE: 'content:manage',
  REPORT_VIEW: 'report:view',
  REPORT_EXPORT: 'report:export',
  STAFF_MANAGE: 'staff:manage',
  AUDIT_VIEW: 'audit:view',
  /// Reading consent history, retention rules, legal holds and erasure records.
  PRIVACY_VIEW: 'privacy:view',
  /// Running an erasure. Destructive and irreversible, so it is its own grant.
  PRIVACY_ERASE: 'privacy:erase',
  /// Placing and releasing legal holds. Deliberately NOT bundled with
  /// PRIVACY_ERASE: one actor able to both suppress an erasure and destroy the
  /// data can force either outcome, and the audit trail reads as routine work
  /// whichever they choose.
  LEGAL_HOLD_MANAGE: 'legal_hold:manage',
  /// Reading the fraud-report queue. Reports name the person who sent them and
  /// often the account they were defrauded from, so this is not bundled with
  /// DASHBOARD_VIEW.
  TRUST_VIEW: 'trust:view',
  /// Triaging a report: changing its status and recording what was done.
  TRUST_MANAGE: 'trust:manage',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

export const ALL_PERMISSIONS = Object.values(Permission);

// ===========================================================================
// Data protection (DPDP Act 2023)
// ===========================================================================

/**
 * What the data is used FOR. Consent is per-purpose because agreeing to be
 * matched to jobs is not agreeing to a background check.
 */
export const ConsentPurpose = {
  KYC_STORAGE: 'KYC_STORAGE',
  IDENTITY_DOCUMENTS: 'IDENTITY_DOCUMENTS',
  EMPLOYER_SHARING: 'EMPLOYER_SHARING',
  BACKGROUND_VERIFICATION: 'BACKGROUND_VERIFICATION',
  COMMUNICATIONS: 'COMMUNICATIONS',
  JOB_ALERTS: 'JOB_ALERTS',
} as const;
export type ConsentPurpose = (typeof ConsentPurpose)[keyof typeof ConsentPurpose];

export const ConsentStatus = {
  GRANTED: 'GRANTED',
  WITHDRAWN: 'WITHDRAWN',
} as const;
export type ConsentStatus = (typeof ConsentStatus)[keyof typeof ConsentStatus];

/**
 * Erasure operates on categories rather than tables: a category spans several
 * tables, and one table can hold more than one category.
 */
export const DataCategory = {
  KYC_IDENTIFIERS: 'KYC_IDENTIFIERS',
  IDENTITY_DOCUMENTS: 'IDENTITY_DOCUMENTS',
  CONTACT_DETAILS: 'CONTACT_DETAILS',
  APPLICATION_HISTORY: 'APPLICATION_HISTORY',
  EMPLOYMENT_RECORDS: 'EMPLOYMENT_RECORDS',
  PAYROLL_RECORDS: 'PAYROLL_RECORDS',
  ATTENDANCE_RECORDS: 'ATTENDANCE_RECORDS',
  AUDIT_TRAIL: 'AUDIT_TRAIL',
} as const;
export type DataCategory = (typeof DataCategory)[keyof typeof DataCategory];

/** Why data survives a withdrawal. The exemption has to name the law. */
export const LegalBasis = {
  EPF_ACT: 'EPF_ACT',
  ESI_ACT: 'ESI_ACT',
  CONTRACT_LABOUR_ACT: 'CONTRACT_LABOUR_ACT',
  INCOME_TAX_ACT: 'INCOME_TAX_ACT',
  SHOPS_AND_ESTABLISHMENTS: 'SHOPS_AND_ESTABLISHMENTS',
  LITIGATION_HOLD: 'LITIGATION_HOLD',
  OTHER: 'OTHER',
} as const;
export type LegalBasis = (typeof LegalBasis)[keyof typeof LegalBasis];

/**
 * Category names as a candidate would recognise them, for the deletion screen.
 *
 * Not the enum names: someone deciding whether to delete their account needs to
 * know that PAYROLL_RECORDS is their payslips, because payslips are the thing
 * they will come back and ask us for.
 */
export const DATA_CATEGORY_LABEL: Record<DataCategory, string> = {
  KYC_IDENTIFIERS: 'Aadhaar, PAN and bank details',
  IDENTITY_DOCUMENTS: 'Uploaded ID documents',
  CONTACT_DETAILS: 'Name, phone number and address',
  APPLICATION_HISTORY: 'Jobs you applied to',
  EMPLOYMENT_RECORDS: 'Your placement and deployment records',
  PAYROLL_RECORDS: 'Payslips, PF and ESI records',
  ATTENDANCE_RECORDS: 'Attendance and shift records',
  AUDIT_TRAIL: 'Security log of changes to your account',
};

/**
 * The statute, named. "We are required to keep this" without saying by what is
 * indistinguishable from "we would rather keep this".
 */
export const LEGAL_BASIS_LABEL: Record<LegalBasis, string> = {
  EPF_ACT: 'Employees’ Provident Funds Act, 1952',
  ESI_ACT: 'Employees’ State Insurance Act, 1948',
  CONTRACT_LABOUR_ACT: 'Contract Labour (Regulation and Abolition) Act, 1970',
  INCOME_TAX_ACT: 'Income-tax Act, 1961',
  SHOPS_AND_ESTABLISHMENTS: 'State Shops and Establishments Act',
  LITIGATION_HOLD: 'An active legal case or dispute',
  OTHER: 'A statutory requirement',
};

export const ErasureReason = {
  CONSENT_WITHDRAWN: 'CONSENT_WITHDRAWN',
  RETENTION_EXPIRED: 'RETENTION_EXPIRED',
  DATA_PRINCIPAL_REQUEST: 'DATA_PRINCIPAL_REQUEST',
  ADMIN_ACTION: 'ADMIN_ACTION',
} as const;
export type ErasureReason = (typeof ErasureReason)[keyof typeof ErasureReason];

export const ErasureStatus = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  /** Some categories erased, others held by statute. Terminal, not a failure. */
  PARTIAL: 'PARTIAL',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
} as const;
export type ErasureStatus = (typeof ErasureStatus)[keyof typeof ErasureStatus];

export const JobAlertKind = {
  PROFILE: 'PROFILE',
  SAVED_SEARCH: 'SAVED_SEARCH',
} as const;
export type JobAlertKind = (typeof JobAlertKind)[keyof typeof JobAlertKind];

export const JobAlertMatchStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  SUPPRESSED: 'SUPPRESSED',
  EXPIRED: 'EXPIRED',
} as const;
export type JobAlertMatchStatus =
  (typeof JobAlertMatchStatus)[keyof typeof JobAlertMatchStatus];

export const ResumeParseStatus = {
  PARSED: 'PARSED',
  FAILED: 'FAILED',
  UNSUPPORTED: 'UNSUPPORTED',
} as const;
export type ResumeParseStatus = (typeof ResumeParseStatus)[keyof typeof ResumeParseStatus];

export const SkillTermStatus = {
  PENDING: 'PENDING',
  MAPPED: 'MAPPED',
  REJECTED: 'REJECTED',
} as const;
export type SkillTermStatus = (typeof SkillTermStatus)[keyof typeof SkillTermStatus];
